import { Autocomplete, TextField } from '@mui/material';
import { VyIcon } from '@vy/ui-design-system';
import { getSelectorAttribute, vyTableSelectors } from '@vy/app-selectors';
import React, { useCallback, useMemo } from 'react';
import type { FilterField } from '../types';

type FilterFieldSelectProps = {
  field: string;
  availableFields: FilterField[];
  onFieldChange: (value: string) => void;
};

export const FilterFieldSelect: React.FC<FilterFieldSelectProps> = React.memo(
  ({ field, availableFields, onFieldChange }) => {
    const handleChange = useCallback(
      (_event: React.SyntheticEvent, value: FilterField | null) => {
        onFieldChange(value?.id || '');
      },
      [onFieldChange],
    );

    const selectedField = useMemo((): FilterField | null => {
      return availableFields.find((f) => f.id === field) ?? null;
    }, [availableFields, field]);

    return (
      <Autocomplete
        {...getSelectorAttribute(vyTableSelectors.filterFieldSelect)}
        size="small"
        value={selectedField as FilterField | undefined}
        onChange={handleChange}
        options={availableFields}
        getOptionLabel={(option) => option.label}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        popupIcon={<VyIcon icon="ArrowDown" />}
        clearIcon={null}
        disableClearable
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder="Select..."
            variant="outlined"
            sx={{
              '& .MuiInputBase-root': {
                border: '1px solid rgba(0, 0, 0, 0.23)',
                borderRadius: '4px',
                width: '170px',
                height: '36px',
                fontSize: '14px',
                backgroundColor: 'white',
                '&:hover': {
                  borderColor: 'rgba(0, 0, 0, 0.87)',
                },
                '&.Mui-focused': {
                  borderColor: '#1976d2',
                  borderWidth: '2px',
                  boxShadow: 'none',
                },
              },
              '& .MuiInputBase-input': {
                color: selectedField ? 'inherit' : '#0d1b27de',
                padding: '8px 12px !important',
              },
              '& .MuiOutlinedInput-notchedOutline': {
                border: 'none',
              },
              '& .MuiFilledInput-root': {
                paddingLeft: '0px',
              },
            }}
          />
        )}
        sx={{
          width: '170px',
          '& .MuiAutocomplete-inputRoot': {
            height: '36px',
          },
          '& .MuiAutocomplete-endAdornment': {
            right: '0 !important',
          },
        }}
      />
    );
  },
);
